// Shared form field: a label + control wrapper. Consolidates the four
// parallel `*-field` / `*-label` systems the app grew (auth, profile,
// equipment, import-review).
//
// By default the control is wrapped *inside* the <label>, which keeps the
// implicit label↔control association several pages rely on (and that tests
// query via getByLabelText). When a field can't wrap its control (e.g. the
// label sits beside a button group) pass `htmlFor` to render a standalone
// <label for=…> over a plain <div> wrapper instead.
//
// Props:
//   label     label text/node
//   htmlFor   when set, renders <label for> + <div> (explicit association)
//   labelExtra optional node rendered next to the label (e.g. a hint/link)
//   variant   visual style: "auth" | "profile" | "eq" | "ir" (default "profile")
//   className extra classes on the wrapper
//   style     passthrough inline style on the wrapper
//   children  the control(s)

const LABEL_CLASS = {
  auth: "auth-label",
  profile: "profile-label",
  eq: "eq-label",
  ir: "ir-field-label",
};

export function Field({
  label,
  htmlFor,
  labelExtra,
  variant = "profile",
  className = "",
  style,
  children,
}) {
  const wrapCls = ["field", `field--${variant}`, className].filter(Boolean).join(" ");
  const labelCls = LABEL_CLASS[variant] ?? LABEL_CLASS.profile;
  const labelInner = (
    <>
      {label}
      {labelExtra}
    </>
  );

  // Explicit association: <label for> sits above a <div> holding the control.
  if (htmlFor) {
    return (
      <div className={wrapCls} style={style}>
        <label className={labelCls} htmlFor={htmlFor}>
          {labelInner}
        </label>
        {children}
      </div>
    );
  }

  // Implicit association: the control lives inside the <label>.
  return (
    <label className={wrapCls} style={style}>
      <span className={labelCls}>{labelInner}</span>
      {children}
    </label>
  );
}
